import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ReportsSectionCompact.css";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import {
  exportSuperAdminStaffPerformanceReport,
  getSuperAdminStaffPerformanceFilters,
  postSuperAdminStaffPerformanceReport,
} from "../api/superadminReports";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import useTableSort from "../hooks/useTableSort";
import { STAFF_PERFORMANCE_TOOLTIPS } from "../constants/reportTooltips";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // Fall back to local time when the timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

const formatNumber = (value, digits = 0) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) {
    return digits > 0 ? (0).toFixed(digits) : "0";
  }
  return num.toFixed(digits);
};

const formatPercent = (value) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "0.0%";
  return `${num.toFixed(1)}%`;
};

const HOURS_KEYS = new Set(["available_hours", "booked_hours", "clocked_hours"]);
const MONEY_KEYS = new Set([
  "net_sales",
  "tips_total",
  "tip_per_ticket",
  "refunds_total",
  "sales_per_hour",
]);
const PERCENT_KEYS = new Set([
  "utilization_pct",
  "tip_rate_pct",
  "refund_rate_pct",
  "rebook_rate_pct",
]);
const COUNT_KEYS = new Set(["ticket_count", "served_clients", "rebooked_clients"]);

const COLUMNS = [
  { key: "staff_name", label: "Staff" },
  { key: "available_hours", label: "Available Hours" },
  { key: "booked_hours", label: "Booked Hours" },
  { key: "clocked_hours", label: "Clocked Hours" },
  { key: "utilization_pct", label: "Utilization %" },
  { key: "net_sales", label: "Net Sales" },
  { key: "ticket_count", label: "Tickets" },
  { key: "tips_total", label: "Tips" },
  { key: "tip_per_ticket", label: "Tip / Ticket" },
  { key: "tip_rate_pct", label: "Tip Rate %" },
  { key: "refunds_total", label: "Refunds" },
  { key: "refund_rate_pct", label: "Refund Rate %" },
  { key: "sales_per_hour", label: "Sales / Hour" },
  { key: "served_clients", label: "Served Clients" },
  { key: "rebooked_clients", label: "Rebooked Clients" },
  { key: "rebook_rate_pct", label: "Rebook Rate %" },
];
const ENTITY_COLUMNS = [
  { key: "business_name", label: "Business" },
  { key: "salon_name", label: "Salon" },
];
const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];
const DEFAULT_HOURS_OPTIONS = [
  { value: "time_clock", label: "Time Clock Hours" },
  { value: "booked", label: "Booked Hours" },
];
const FIELD_TOOLTIPS = {
  scope:
    "Run across all salons, one business, or one salon. Accounting dates use salon-local time.",
  business: "Required in Business scope. Optional narrowing when scope is All.",
  salon: "Required in Salon scope. Optional narrowing in Business scope.",
  include_business_salon_cols:
    "Shows Business and Salon columns at the start of each row and CSV.",
  employee_loader:
    "For broader scopes, load a filtered employee list only when needed.",
};

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "staff-performance",
  title: "Staff Performance (Super Admin)",
  subtitle:
    "Compare utilization, sales, tips, and rebooking metrics across the selected scope.",
  summaryLabel: "Performance Summary",
  detailsLabel: "Staff Detail Table",
  hasExport: true,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("staff-performance");

export default function SuperAdminStaffPerformance() {
  const navigate = useNavigate();
  const { is_super_admin, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [includeBusinessSalonCols, setIncludeBusinessSalonCols] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [hoursBasisOptions, setHoursBasisOptions] = useState(DEFAULT_HOURS_OPTIONS);
  const [employeesLoaded, setEmployeesLoaded] = useState(false);
  const [employeeSearchInput, setEmployeeSearchInput] = useState("");
  const [employeeSearchApplied, setEmployeeSearchApplied] = useState("");
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState("");
  const [reportTzName, setReportTzName] = useState("");

  const broadScope = scope === "all" || scope === "business";
  const showEntityColumns = broadScope && includeBusinessSalonCols;

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((entry) => {
      if (!entry) return;
      map.set(String(entry.id), entry);
    });
    return map;
  }, [businesses]);
  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((entry) => {
      if (!entry) return;
      map.set(String(entry.id), entry);
    });
    return map;
  }, [salons]);
  const anchorTimezone = useMemo(() => {
    if (salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      if (selectedSalon?.timezone) return selectedSalon.timezone;
    }
    if (businessId) {
      const selectedBusiness = businessMap.get(String(businessId));
      if (selectedBusiness?.timezone) return selectedBusiness.timezone;
    }
    return authSalonTimezone || "UTC";
  }, [salonId, salonMap, businessId, businessMap, authSalonTimezone]);
  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return (salons || []).filter(
      (entry) => String(entry.business_id || "") === String(businessId)
    );
  }, [salons, businessId]);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/superadmin/reports", { replace: true });
  };
  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip
          text={FIELD_TOOLTIPS[tooltipKey] || STAFF_PERFORMANCE_TOOLTIPS[tooltipKey]}
          label={label}
        />
      ) : null}
    </div>
  );

  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [startDate, setStartDate] = useState(() =>
    formatDateISO(addDays(new Date(), -6), authSalonTimezone || "UTC")
  );
  const [endDate, setEndDate] = useState(() =>
    formatDateISO(new Date(), authSalonTimezone || "UTC")
  );
  const [employeeIds, setEmployeeIds] = useState([]);
  const [rebookWindowDays, setRebookWindowDays] = useState(30);
  const [hoursBasis, setHoursBasis] = useState("time_clock");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hasAutoRunRef = useRef(false);

  useEffect(() => {
    setIncludeBusinessSalonCols(scope !== "salon");
    setEmployeesLoaded(scope === "salon");
    setEmployeeSearchInput("");
    setEmployeeSearchApplied("");
    setEmployeeIds([]);
    hasAutoRunRef.current = false;
  }, [scope]);

  useEffect(() => {
    if (scope === "salon") {
      setBusinessId("");
    }
  }, [scope]);

  useEffect(() => {
    if (!salonId || !businessId) return;
    const selectedSalon = salonMap.get(String(salonId));
    if (!selectedSalon) {
      setSalonId("");
      return;
    }
    if (String(selectedSalon.business_id || "") !== String(businessId)) {
      setSalonId("");
    }
  }, [businessId, salonId, salonMap]);

  const loadFilters = useCallback(
    async (includeEmployees) => {
      if (!is_super_admin) return;
      setFiltersLoading(true);
      setFiltersError("");
      setStaffLoading(Boolean(includeEmployees));
      try {
        const data = await getSuperAdminStaffPerformanceFilters({
          scope,
          business_id: businessId || undefined,
          salon_id: salonId || undefined,
          include_employees: includeEmployees ? true : undefined,
          employee_search:
            includeEmployees && employeeSearchApplied
              ? employeeSearchApplied
              : undefined,
        });
        setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
        setSalons(Array.isArray(data?.salons) ? data.salons : []);
        setStaffOptions(includeEmployees && Array.isArray(data?.employees) ? data.employees : []);
        const nextHours = Array.isArray(data?.hours_basis_options)
          ? data.hours_basis_options
          : DEFAULT_HOURS_OPTIONS;
        setHoursBasisOptions(nextHours.length ? nextHours : DEFAULT_HOURS_OPTIONS);
      } catch (err) {
        setBusinesses([]);
        setSalons([]);
        setStaffOptions([]);
        setFiltersError(err?.message || "Unable to load report filters.");
      } finally {
        setFiltersLoading(false);
        setStaffLoading(false);
      }
    },
    [is_super_admin, scope, businessId, salonId, employeeSearchApplied]
  );

  useEffect(() => {
    const includeEmployees =
      (scope === "salon" && Boolean(salonId)) || (scope !== "salon" && employeesLoaded);
    loadFilters(includeEmployees);
  }, [scope, businessId, salonId, employeesLoaded, employeeSearchApplied, loadFilters]);

  useEffect(() => {
    if ((scope === "all" || scope === "business") && !employeesLoaded) {
      setStaffOptions([]);
    }
  }, [scope, employeesLoaded]);

  useEffect(() => {
    if (!hoursBasisOptions.length) return;
    if (hoursBasisOptions.some((entry) => entry.value === hoursBasis)) return;
    setHoursBasis(hoursBasisOptions[0].value || "time_clock");
  }, [hoursBasisOptions, hoursBasis]);

  const dateRangeError = useMemo(() => {
    if (!startDate || !endDate) return "";
    return endDate < startDate ? "End date cannot be before start date." : "";
  }, [startDate, endDate]);

  const payloadBase = useMemo(
    () => ({
      scope,
      business_id: businessId ? Number(businessId) : undefined,
      salon_id: salonId ? Number(salonId) : undefined,
      date_from: startDate,
      date_to: endDate,
      employee_ids: employeeIds.length ? employeeIds : null,
      rebook_window_days: Math.max(Number(rebookWindowDays) || 0, 0),
      hours_basis: hoursBasis,
      include_business_salon_cols: showEntityColumns,
    }),
    [
      scope,
      businessId,
      salonId,
      startDate,
      endDate,
      employeeIds,
      rebookWindowDays,
      hoursBasis,
      showEntityColumns,
    ]
  );

  const validateScope = useCallback(() => {
    if (scope === "business" && !businessId) {
      return "Choose a business for Business scope.";
    }
    if (scope === "salon" && !salonId) {
      return "Choose a salon for Salon scope.";
    }
    if (dateRangeError) {
      return dateRangeError;
    }
    return "";
  }, [scope, businessId, salonId, dateRangeError]);

  const fetchReport = useCallback(
    async (payloadOverride = null) => {
      const validationError = validateScope();
      if (validationError) {
        setError(validationError);
        return;
      }
      const requestPayload = payloadOverride || payloadBase;
      setLoading(true);
      setError("");
      try {
        const data = await postSuperAdminStaffPerformanceReport(requestPayload);
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setSummary(data?.summary || null);
        setReportTzName(data?.tz_name || "");
      } catch (err) {
        setRows([]);
        setSummary(null);
        setReportTzName("");
        setError(err?.message || "Unable to load staff performance report.");
      } finally {
        setLoading(false);
      }
    },
    [payloadBase, validateScope]
  );

  useEffect(() => {
    if (!is_super_admin || hasAutoRunRef.current) return;
    if (scope === "business" && !businessId) return;
    if (scope === "salon" && !salonId) return;
    if (dateRangeError) return;
    hasAutoRunRef.current = true;
    fetchReport(payloadBase);
  }, [is_super_admin, scope, businessId, salonId, dateRangeError, fetchReport, payloadBase]);

  const handleToggleEmployee = (staffId) => {
    setEmployeeIds((prev) =>
      prev.includes(staffId)
        ? prev.filter((entry) => entry !== staffId)
        : [...prev, staffId]
    );
  };

  const handleLoadEmployees = () => {
    setEmployeesLoaded(true);
    setEmployeeSearchApplied(employeeSearchInput.trim());
    setEmployeeIds([]);
  };

  const handleScopeChange = (value) => {
    setScope(value);
    if (value === "all") {
      setSalonId("");
    }
    if (value === "business") {
      setSalonId("");
    }
  };

  const handleBusinessChange = (value) => {
    setBusinessId(value);
    if (!value && scope !== "salon") {
      setSalonId("");
    }
    if (!salonId) return;
    const selectedSalon = salonMap.get(String(salonId));
    if (
      selectedSalon &&
      value &&
      String(selectedSalon.business_id || "") !== String(value)
    ) {
      setSalonId("");
    }
  };

  const handleSalonChange = (value) => {
    setSalonId(value);
    if (!value) return;
    const selectedSalon = salonMap.get(String(value));
    if (selectedSalon?.business_id) {
      setBusinessId(String(selectedSalon.business_id));
    }
  };

  const handleExport = async () => {
    const validationError = validateScope();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError("");
    try {
      await exportSuperAdminStaffPerformanceReport(payloadBase);
    } catch (err) {
      setError(err?.message || "Unable to export staff performance CSV.");
    } finally {
      setLoading(false);
    }
  };

  const tiles = useMemo(
    () => [
      { label: "Staff", value: summary?.staff_count ?? rows.length },
      { label: "Net Sales", value: formatMoney(summary?.total_net_sales) },
      { label: "Sales / Hour", value: formatMoney(summary?.overall_sales_per_hour) },
      { label: "Utilization", value: formatPercent(summary?.overall_utilization_pct) },
      { label: "Rebook Rate", value: formatPercent(summary?.overall_rebook_rate_pct) },
      { label: "Tickets", value: formatNumber(summary?.total_tickets, 0) },
    ],
    [summary, rows.length]
  );

  const formatCellValue = (row, key) => {
    const value = row?.[key];
    if (MONEY_KEYS.has(key)) return formatMoney(value);
    if (PERCENT_KEYS.has(key)) return formatPercent(value);
    if (HOURS_KEYS.has(key)) return formatNumber(value, 2);
    if (COUNT_KEYS.has(key)) return formatNumber(value, 0);
    return value ?? "--";
  };

  const tableColumns = useMemo(
    () => (showEntityColumns ? [...ENTITY_COLUMNS, ...COLUMNS] : COLUMNS),
    [showEntityColumns]
  );

  const columnsMap = useMemo(() => {
    const map = {};
    tableColumns.forEach((col) => {
      let type = "text";
      if (HOURS_KEYS.has(col.key)) type = "number";
      if (MONEY_KEYS.has(col.key)) type = "number";
      if (PERCENT_KEYS.has(col.key)) type = "percent";
      if (COUNT_KEYS.has(col.key)) type = "number";
      map[col.key] = { type, getValue: (row) => row?.[col.key] };
    });
    return map;
  }, [tableColumns]);

  const staffSort = useTableSort({
    rows,
    columns: columnsMap,
    fallbackKey: "staff_name",
  });

  const timezoneLine = useMemo(() => {
    if (scope === "salon") {
      const tzLabel =
        salonMap.get(String(salonId))?.timezone || reportTzName || anchorTimezone || "UTC";
      return `Salon timezone: ${tzLabel}`;
    }
    return "Accounting dates use each salon's local timezone.";
  }, [scope, salonMap, salonId, reportTzName, anchorTimezone]);

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">Staff Performance</div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {filtersError && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-700">
                {filtersError}
              </div>
            )}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}

            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Scope", "scope")}
                  <select
                    value={scope}
                    onChange={(event) => handleScopeChange(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filtersLoading}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Business", "business")}
                  <select
                    value={businessId}
                    onChange={(event) => handleBusinessChange(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filtersLoading || scope === "salon"}
                  >
                    <option value="">All Businesses</option>
                    {(businesses || []).map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.code} - {business.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Salon", "salon")}
                  <select
                    value={salonId}
                    onChange={(event) => handleSalonChange(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filtersLoading || !(scope === "salon" || scope === "business")}
                  >
                    <option value="">
                      {scope === "salon"
                        ? "Select salon"
                        : scope === "business"
                        ? "All salons in business"
                        : "Salon filter disabled for this scope"}
                    </option>
                    {(filteredSalons || []).map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="rounded-2xl border border-slate-200 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <input
                      id="include-business-salon-cols"
                      type="checkbox"
                      checked={includeBusinessSalonCols}
                      onChange={(event) =>
                        setIncludeBusinessSalonCols(Boolean(event.target.checked))
                      }
                      disabled={!broadScope}
                    />
                    <label
                      htmlFor="include-business-salon-cols"
                      className="text-sm font-medium text-slate-700"
                    >
                      Show Business/Salon columns
                    </label>
                    <InfoTooltip
                      text={FIELD_TOOLTIPS.include_business_salon_cols}
                      label="Show Business/Salon columns"
                    />
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    {broadScope
                      ? "Shown at the start of table rows and CSV export."
                      : "Only available in All Salons or Business scope."}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Date From", "start_date")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Date To", "end_date")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Rebook Window (days)", "rebook_window")}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={rebookWindowDays}
                    onChange={(e) => setRebookWindowDays(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Hours Basis", "hours_basis")}
                  <select
                    value={hoursBasis}
                    onChange={(e) => setHoursBasis(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    {(hoursBasisOptions || []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  {renderFieldLabel("Employees", "employees")}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                      onClick={() => setEmployeeIds([])}
                    >
                      Clear
                    </button>
                    {broadScope && (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={employeeSearchInput}
                          onChange={(event) => setEmployeeSearchInput(event.target.value)}
                          placeholder="Search employee name"
                          className={`${FIELD_CLASS} h-9 w-[200px] text-sm`}
                        />
                        <button
                          type="button"
                          onClick={handleLoadEmployees}
                          className={`${BTN_OUTLINE} h-9 px-3 text-sm`}
                          disabled={staffLoading}
                        >
                          {employeesLoaded ? "Search" : "Load Employees"}
                        </button>
                        <InfoTooltip
                          text={FIELD_TOOLTIPS.employee_loader}
                          label="Load Employees"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 max-h-44 overflow-y-auto space-y-2">
                  {(scope === "salon" || employeesLoaded) &&
                    staffOptions.map((staff) => {
                      const staffLabel =
                        broadScope && showEntityColumns
                          ? `${staff.display_name} (${staff.business_name || "Business"} / ${
                              staff.salon_name || "Salon"
                            })`
                          : staff.display_name;
                      return (
                        <label
                          key={staff.id}
                          className="flex items-center gap-2 text-sm text-slate-700"
                        >
                          <input
                            type="checkbox"
                            checked={employeeIds.includes(staff.id)}
                            onChange={() => handleToggleEmployee(staff.id)}
                          />
                          {staffLabel}
                        </label>
                      );
                    })}
                  {staffLoading || filtersLoading ? (
                    <div className="text-sm text-slate-500">Loading employees...</div>
                  ) : scope === "salon" && !salonId ? (
                    <div className="text-sm text-slate-500">Select a salon to load employees.</div>
                  ) : scope !== "salon" && !employeesLoaded ? (
                    <div className="text-sm text-slate-500">
                      All employees are included. Click Load Employees to select specific staff.
                    </div>
                  ) : !staffOptions.length ? (
                    <div className="text-sm text-slate-500">No employees found.</div>
                  ) : null}
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  {employeeIds.length ? `${employeeIds.length} selected` : "All employees"}
                </div>
              </div>

              {dateRangeError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                  {dateRangeError}
                </div>
              )}

              <div
                id={HELP_ANCHORS.export}
                className={`flex flex-wrap items-center gap-3 rounded-2xl ${getSectionClassName(
                  HELP_ANCHORS.export
                )}`}
              >
                <div className="text-xs text-slate-500">{timezoneLine}</div>
                <div className="ml-auto flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fetchReport(payloadBase)}
                    className={`${BTN_PRIMARY} min-w-[140px]`}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Run Report"}
                  </button>
                  <button
                    type="button"
                    onClick={handleExport}
                    className={`${BTN_OUTLINE} min-w-[140px]`}
                    disabled={loading}
                  >
                    Export CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/superadmin/reports")}
                    className={`${BTN_OUTLINE} min-w-[140px]`}
                  >
                    Back to Reports
                  </button>
                </div>
              </div>
            </div>

            <div
              id={HELP_ANCHORS.kpis}
              className={`grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4 ${getSectionClassName(
                HELP_ANCHORS.kpis
              )}`}
            >
              {tiles.map((tile) => (
                <div
                  key={tile.label}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    {tile.label}
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">
                    {tile.value}
                  </div>
                </div>
              ))}
            </div>

            <div
              id={HELP_ANCHORS.details}
              className={`staff-perf-table rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(
                HELP_ANCHORS.details
              )}`}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      {tableColumns.map((col) => (
                        <SortableTh
                          key={col.key}
                          label={col.label}
                          sortKey={col.key}
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          tooltip={STAFF_PERFORMANCE_TOOLTIPS[`column_${col.key}`]}
                          className="px-4 py-3 text-center"
                          align="center"
                        />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {staffSort.sortedRows.map((row) => (
                      <tr
                        key={`${row.salon_id || "salon"}-${row.staff_id}`}
                        className="border-t border-slate-100"
                      >
                        {tableColumns.map((col) => (
                          <td
                            key={`${row.salon_id || "salon"}-${row.staff_id}-${col.key}`}
                            className="px-4 py-3 text-center text-slate-700"
                          >
                            {formatCellValue(row, col.key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {!staffSort.sortedRows.length && (
                      <tr>
                        <td
                          colSpan={tableColumns.length}
                          className="px-4 py-8 text-center text-slate-500"
                        >
                          {loading ? "Loading staff performance..." : "No results for this range."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
